import PageLayout from "../../layouts/PageLayout.tsx";
import { type Escrow } from "../../repository/EscrowRepository.ts";
import { Card, SubCard } from "../../components/card/Card.tsx";
import { PartyRecapTable } from "../../components/Recap.tsx";
import { type Address } from "viem";
import Timeline from "../../components/Timeline.tsx";
import TransactionsTable from "../../components/table/TransactionsTable.tsx";
import { getFormattedStatus } from "../../utils/getFormattedStatus.ts";
import {
  useReadErc20Allowance,
  useReadErc20BalanceOf,
  useWriteErc20Approve,
  useWriteEscrowApprove,
  useWriteEscrowCancelBeforeStart,
  useWriteEscrowDepositAssets,
  useWriteEscrowDepositPenalty,
  useWriteEscrowExpire,
  useWriteEscrowReject,
} from "../../generated.ts";
import { usePublicClient } from "wagmi";
import type { Token } from "../../repository/TokenWhitelistRepository.ts";
import toast from "react-hot-toast";
import { useState } from "react";

type Role = "SELLER" | "BUYER" | "VIEWER";
const shouldDepositPenaltyLogic: Record<Role, (escrow: Escrow) => boolean> = {
  SELLER: (escrow) => escrow.status == 0 && !escrow.seller_penalty_deposited,
  BUYER: (escrow) => escrow.status == 0 && !escrow.buyer_penalty_deposited,
  VIEWER: () => false,
};
const shouldDepositAssetLogic: Record<
  Role,
  (escrow: Escrow, isExpired: boolean) => boolean
> = {
  SELLER: (escrow, isExpired) =>
    escrow.status == 1 && !escrow.seller_assets_deposited && !isExpired,
  BUYER: (escrow, isExpired) =>
    escrow.status == 1 && !escrow.buyer_assets_deposited && !isExpired,
  VIEWER: () => false,
};
const shouldDoActionsLogic: Record<Role, () => boolean> = {
  SELLER: () => true,
  BUYER: () => true,
  VIEWER: () => false,
};
const shouldExpireLogic: Record<
  Role,
  (escrow: Escrow, isExpired: boolean) => boolean
> = {
  SELLER: (escrow, isExpired) => escrow.status == 1 && isExpired,
  BUYER: (escrow, isExpired) => escrow.status == 1 && isExpired,
  VIEWER: () => false,
};
const shouldTakeDecisionPostDepositsLogic = (
  escrow: Escrow,
  isExpired: boolean,
) => {
  return (
    escrow.status == 1 &&
    escrow.seller_assets_deposited &&
    escrow.buyer_assets_deposited &&
    !isExpired
  );
};
const canCancelBeforeStartLogic: Record<Role, (escrow: Escrow) => boolean> = {
  SELLER: (escrow) => escrow.status == 0,
  BUYER: (escrow) => escrow.status == 0,
  VIEWER: () => false,
};

const getPenaltyToken = (escrow: Escrow, role: Role) => {
  if (role == "SELLER") return escrow.seller_penalty_token;
  if (role == "BUYER") return escrow.buyer_penalty_token;
  return null;
};
const getExchangeToken = (escrow: Escrow, role: Role) => {
  if (role == "SELLER") return escrow.seller_exchange_token;
  if (role == "BUYER") return escrow.buyer_exchange_token;
  return null;
};
const getFeeToken = (escrow: Escrow, role: Role) => {
  if (role == "SELLER") return escrow.seller_fee_token;
  if (role == "BUYER") return escrow.buyer_fee_token;
  return null;
};
const getPenaltyAmount = (escrow: Escrow, role: Role) => {
  if (role == "SELLER") return escrow.seller_penalty_amount;
  if (role == "BUYER") return escrow.buyer_penalty_amount;
  return null;
};
const getExchangeAmount = (escrow: Escrow, role: Role) => {
  if (role == "SELLER") return escrow.seller_exchange_amount;
  if (role == "BUYER") return escrow.buyer_exchange_amount;
  return null;
};
const getFeeAmount = (escrow: Escrow, role: Role) => {
  if (role == "SELLER") return escrow.seller_fee_amount;
  if (role == "BUYER") return escrow.buyer_fee_amount;
  return null;
};
const getColoredTimelineStatus = (status: number) => {
  if (status == 0) return undefined;
  if (status == 1) return undefined;
  if (status == 2) return undefined;
  return "bg-error";
};

const EscrowPage = ({
  escrow,
  address,
  userAddress,
  role,
}: {
  escrow: Escrow;
  address: Address;
  userAddress: Address;
  role: Role;
}) => {
  const {
    sellerPenaltyTokenApprovedAmount,
    sellerExchangeTokenApprovedAmount,
    sellerFeeTokenApprovedAmount,
    buyerPenaltyTokenApprovedAmount,
    buyerExchangeTokenApprovedAmount,
    buyerFeeTokenApprovedAmount,
    sellerPenaltyTokenBalance,
    sellerExchangeTokenBalance,
    sellerFeeTokenBalance,
    buyerPenaltyTokenBalance,
    buyerExchangeTokenBalance,
    buyerFeeTokenBalance,
  } = useAuthorizations(
    userAddress,
    address,
    escrow.seller_penalty_token,
    escrow.seller_exchange_token,
    escrow.seller_fee_token,
    escrow.buyer_penalty_token,
    escrow.buyer_exchange_token,
    escrow.buyer_fee_token,
  );

  const publicClient = usePublicClient();
  const { writeContractAsync: depositPenalty } = useWriteEscrowDepositPenalty();
  const [isPendingAction, setPendingAction] = useState<boolean>(false);

  const { writeContractAsync: depositExchange } = useWriteEscrowDepositAssets();

  const { writeContractAsync: approveExchange } = useWriteEscrowApprove();

  const { writeContractAsync: rejectExchange } = useWriteEscrowReject();

  const { writeContractAsync: expireExchange } = useWriteEscrowExpire();

  const { writeContractAsync: approveERC20 } = useWriteErc20Approve();

  const { writeContractAsync: cancelBeforeStart } =
    useWriteEscrowCancelBeforeStart();

  if (address == undefined) {
    return null;
  }

  if (!escrow) {
    return null;
  }
  const isExpired = parseInt(escrow.deadline) * 1000 < Date.now();

  const shouldDepositPenalty = shouldDepositPenaltyLogic[role](escrow);
  const shouldDepositAsset = shouldDepositAssetLogic[role](escrow, isExpired);
  const shouldDoActions = shouldDoActionsLogic[role]();
  const shouldTakeDecisionPostDeposits = shouldTakeDecisionPostDepositsLogic(
    escrow,
    isExpired,
  );
  const shouldExpire = shouldExpireLogic[role](escrow, isExpired);
  const canCancelBeforeStart = canCancelBeforeStartLogic[role](escrow);
  const formatedStatus = getFormattedStatus(escrow.status);
  const numberOfPenaltyDeposits = [
    escrow.seller_penalty_deposited,
    escrow.buyer_penalty_deposited,
  ].filter(Boolean).length;
  const numberOfAssetDeposits = [
    escrow.seller_assets_deposited,
    escrow.buyer_assets_deposited,
  ].filter(Boolean).length;
  const exchangeCompleted = formatedStatus.id >= 2;

  const handleApproval = async (
    balance: bigint,
    approvedAmount: bigint,
    amount: bigint,
    token: Token,
  ) => {
    if (!publicClient) return false;

    if (balance < amount) {
      toast.error("Insufficient balance.");
      return false;
    }
    if (approvedAmount < amount) {
      setPendingAction(true);
      let txid;
      try {
        txid = await approveERC20({
          address: token.address as Address,
          args: [escrow.escrow_address as Address, amount],
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_) {
        setPendingAction(false);
        return;
      }

      await toast
        .promise(
          publicClient.waitForTransactionReceipt({
            hash: txid,
          }),
          {
            loading: "Approving token...",
            success: "Token approved.",
            error: "Error while approving the token.",
          },
        )
        .finally(() => setPendingAction(false));
    }
    return true;
  };

  const handleDepositPenalty = async () => {
    if (!address) return;
    if (!publicClient) return;

    const penaltyToken = getPenaltyToken(escrow, role);
    const penaltyAmount = getPenaltyAmount(escrow, role);

    if (!penaltyToken || !penaltyAmount) {
      return;
    }

    if (role == "SELLER") {
      const success = await handleApproval(
        sellerPenaltyTokenBalance,
        sellerPenaltyTokenApprovedAmount,
        penaltyAmount,
        penaltyToken,
      );
      if (!success) return;
    } else if (role == "BUYER") {
      const success = await handleApproval(
        buyerPenaltyTokenBalance,
        buyerPenaltyTokenApprovedAmount,
        penaltyAmount,
        penaltyToken,
      );
      if (!success) return;
    }

    setPendingAction(true);
    let txid;
    try {
      txid = await depositPenalty({ address: address as Address });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setPendingAction(false);
      return;
    }

    await toast
      .promise(
        publicClient.waitForTransactionReceipt({
          hash: txid,
          confirmations: 2,
        }),
        {
          loading: "Sending penalty deposit...",
          success: "Transaction confirmed.",
          error: "Error during the transaction.",
        },
      )
      .finally(() => setPendingAction(false));
  };

  const handleDepositExchange = async () => {
    if (!address) return;
    if (!publicClient) return;

    const exchangeToken = getExchangeToken(escrow, role);
    const exchangeAmount = getExchangeAmount(escrow, role);
    const feeToken = getFeeToken(escrow, role);
    const feeAmount = getFeeAmount(escrow, role);

    if (!exchangeToken || !exchangeAmount || !feeToken || !feeAmount) {
      return;
    }

    if (role == "SELLER") {
      if (exchangeToken.address === feeToken.address) {
        const totalAmount = BigInt(exchangeAmount) + BigInt(feeAmount);
        const success = await handleApproval(
          sellerExchangeTokenBalance,
          sellerExchangeTokenApprovedAmount,
          totalAmount,
          exchangeToken,
        );
        if (!success) return;
      } else {
        let success = await handleApproval(
          sellerExchangeTokenBalance,
          sellerExchangeTokenApprovedAmount,
          exchangeAmount,
          exchangeToken,
        );
        if (!success) return;

        success = await handleApproval(
          sellerFeeTokenBalance,
          sellerFeeTokenApprovedAmount,
          feeAmount,
          feeToken,
        );
        if (!success) return;
      }
    } else if (role == "BUYER") {
      if (exchangeToken.address === feeToken.address) {
        const totalAmount = exchangeAmount + feeAmount;
        const success = await handleApproval(
          buyerExchangeTokenBalance,
          buyerExchangeTokenApprovedAmount,
          totalAmount,
          exchangeToken,
        );
        if (!success) return;
      } else {
        let success = await handleApproval(
          buyerExchangeTokenBalance,
          buyerExchangeTokenApprovedAmount,
          exchangeAmount,
          exchangeToken,
        );
        if (!success) return;

        success = await handleApproval(
          buyerFeeTokenBalance,
          buyerFeeTokenApprovedAmount,
          feeAmount,
          feeToken,
        );
        if (!success) return;
      }
    }

    setPendingAction(true);
    let txid;
    try {
      txid = await depositExchange({ address: address as Address });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setPendingAction(false);
      return;
    }

    await toast
      .promise(
        publicClient.waitForTransactionReceipt({
          hash: txid,
          confirmations: 2,
        }),
        {
          loading: "Sending exchange deposit...",
          success: "Transaction confirmed.",
          error: "Error during the transaction.",
        },
      )
      .finally(() => setPendingAction(false));
  };

  const handleCancelBeforeStart = async () => {
    if (!address) return;
    if (!publicClient) return;

    setPendingAction(true);
    let txid;
    try {
      txid = await cancelBeforeStart({
        address: address as Address,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setPendingAction(false);
      return;
    }

    await toast
      .promise(
        publicClient.waitForTransactionReceipt({
          hash: txid,
          confirmations: 2,
        }),
        {
          loading: "Cancelling escrow...",
          success: "Escrow successfully cancelled.",
          error: "Error while cancelling the escrow.",
        },
      )
      .finally(() => setPendingAction(false));
  };

  const handleApprove = async () => {
    if (!address) return;
    if (!publicClient) return;

    setPendingAction(true);
    let txid;
    try {
      txid = await approveExchange({ address: address as Address });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setPendingAction(false);
      return;
    }

    await toast
      .promise(
        publicClient.waitForTransactionReceipt({
          hash: txid,
          confirmations: 2,
        }),
        {
          loading: "Approving exchange...",
          success: "Exchange successfully approved.",
          error: "Error while approving the exchange.",
        },
      )
      .finally(() => setPendingAction(false));
  };

  const handleReject = async () => {
    if (!address) return;
    if (!publicClient) return;

    setPendingAction(true);
    let txid;
    try {
      txid = await rejectExchange({ address: address as Address });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setPendingAction(false);
      return;
    }

    await toast
      .promise(
        publicClient.waitForTransactionReceipt({
          hash: txid,
          confirmations: 2,
        }),
        {
          loading: "Rejecting exchange...",
          success: "Exchange successfully rejected.",
          error: "Error while rejecting the exchange.",
        },
      )
      .finally(() => setPendingAction(false));
  };

  const handleExpire = async () => {
    if (!address) return;
    if (!publicClient) return;

    setPendingAction(true);
    let txid;
    try {
      txid = await expireExchange({ address: address as Address });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setPendingAction(false);
      return;
    }

    await toast
      .promise(
        publicClient.waitForTransactionReceipt({
          hash: txid,
          confirmations: 2,
        }),
        {
          loading: "Expiring exchange...",
          success: "Exchange successfully expired.",
          error: "Error while expiring the exchange.",
        },
      )
      .finally(() => setPendingAction(false));
  };

  const timelineEndColor = getColoredTimelineStatus(escrow.status);

  return (
    <PageLayout
      breadcrumbs={[{ name: "Escrow", href: "/" }, { name: address as string }]}
    >
      <div className="flex flex-col gap-4">
        <Card title={`Actions`}>
          <div className={"flex gap-4"}>
            <SubCard title={"Timeline"} className={"flex-1"}>
              <Timeline
                items={[
                  { label: "Contract initialization", done: true },
                  {
                    label: `Penalty deposits\n (${numberOfPenaltyDeposits}/2)`,
                    done: numberOfPenaltyDeposits === 2,
                  },
                  {
                    label: `Exchange deposits\n (${numberOfAssetDeposits}/2)`,
                    done: numberOfAssetDeposits === 2,
                  },
                  {
                    label: `Exchange outcome\n (${!exchangeCompleted ? "Approval, Rejection, Expiration" : formatedStatus.name})`,
                    done: exchangeCompleted,
                    color: timelineEndColor,
                  },
                ]}
                deadline={escrow.deadline}
              />
            </SubCard>
            {shouldDoActions && (
              <SubCard title={"Available actions"} className={"w-75"}>
                <div className="flex flex-col gap-1.5">
                  {shouldDepositPenalty && (
                    <button
                      className="btn btn-primary btn-block text-white"
                      onClick={handleDepositPenalty}
                      disabled={isPendingAction}
                    >
                      Deposit penalties
                    </button>
                  )}
                  {canCancelBeforeStart && (
                    <button
                      className="btn btn-warning btn-block text-white"
                      onClick={handleCancelBeforeStart}
                      disabled={isPendingAction}
                    >
                      Cancel amicably
                    </button>
                  )}
                  {shouldDepositAsset && (
                    <button
                      className="btn btn-primary btn-block text-white"
                      onClick={handleDepositExchange}
                      disabled={isPendingAction}
                    >
                      Deposit exchange amounts
                    </button>
                  )}
                  {shouldTakeDecisionPostDeposits && (
                    <>
                      <button
                        className="btn btn-success btn-block text-white"
                        onClick={handleApprove}
                        disabled={isPendingAction}
                      >
                        Approve exchange
                      </button>
                      <button
                        className="btn btn-error btn-block text-white"
                        onClick={handleReject}
                        disabled={isPendingAction}
                      >
                        Reject exchange
                      </button>
                    </>
                  )}
                  {shouldExpire && (
                    <button
                      className="btn btn-error btn-block text-white"
                      onClick={handleExpire}
                      disabled={isPendingAction}
                    >
                      Expire exchange
                    </button>
                  )}

                  {!(
                    canCancelBeforeStart ||
                    shouldDepositPenalty ||
                    shouldDepositAsset ||
                    shouldTakeDecisionPostDeposits ||
                    shouldExpire
                  ) && (
                    <div
                      role="alert"
                      className="alert alert-soft alert-warning"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 shrink-0 stroke-current"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span>No action possible at this time</span>
                    </div>
                  )}
                </div>
              </SubCard>
            )}
          </div>
        </Card>
        <Card title={"Latest transactions"}>
          <SubCard>
            <TransactionsTable
              address={escrow.escrow_address}
              sellerAddress={escrow.seller_account}
              sellerNickname={escrow.seller_nickname}
              buyerNickname={escrow.buyer_nickname}
            />
          </SubCard>
        </Card>
        <Card title={"Escrow summary"}>
          <div className="flex flex-col gap-4">
            <SubCard title={"Buyer"}>
              <PartyRecapTable
                nickname={escrow.buyer_nickname}
                isLoading={false}
                address={escrow.buyer_account}
                willReceiveToken={escrow.seller_exchange_token}
                willReceiveAmount={escrow.seller_exchange_amount}
                tradeToken={escrow.buyer_exchange_token}
                tradeAmount={escrow.buyer_exchange_amount}
                depositToken={escrow.buyer_penalty_token}
                depositAmount={escrow.buyer_penalty_amount}
                feeToken={escrow.buyer_fee_token}
                feeAmount={escrow.buyer_fee_amount}
              />
            </SubCard>
            <SubCard title={"Seller"}>
              <PartyRecapTable
                nickname={escrow.seller_nickname}
                isLoading={false}
                address={escrow.seller_account}
                willReceiveToken={escrow.buyer_exchange_token}
                willReceiveAmount={escrow.buyer_exchange_amount}
                tradeToken={escrow.seller_exchange_token}
                tradeAmount={escrow.seller_exchange_amount}
                depositToken={escrow.seller_penalty_token}
                depositAmount={escrow.seller_penalty_amount}
                feeToken={escrow.seller_fee_token}
                feeAmount={escrow.seller_fee_amount}
              />
            </SubCard>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
};

const useAuthorizations = (
  userAddress: Address,
  address: Address,
  sellerPenaltyToken: Token,
  sellerExchangeToken: Token,
  sellerFeeToken: Token,
  buyerPenaltyToken: Token,
  buyerExchangeToken: Token,
  buyerFeeToken: Token,
) => {
  const { data: sellerPenaltyTokenApprovedAmount } = useReadErc20Allowance({
    args: [userAddress, address],
    address: sellerPenaltyToken.address as Address,
  });
  const { data: sellerExchangeTokenApprovedAmount } = useReadErc20Allowance({
    args: [userAddress, address],
    address: sellerExchangeToken.address as Address,
  });
  const { data: sellerFeeTokenApprovedAmount } = useReadErc20Allowance({
    args: [userAddress, address],
    address: sellerFeeToken.address as Address,
  });
  const { data: buyerPenaltyTokenApprovedAmount } = useReadErc20Allowance({
    args: [userAddress, address],
    address: buyerPenaltyToken.address as Address,
  });
  const { data: buyerExchangeTokenApprovedAmount } = useReadErc20Allowance({
    args: [userAddress, address],
    address: buyerExchangeToken.address as Address,
  });
  const { data: buyerFeeTokenApprovedAmount } = useReadErc20Allowance({
    args: [userAddress, address],
    address: buyerFeeToken.address as Address,
  });

  const { data: sellerPenaltyTokenBalance } = useReadErc20BalanceOf({
    args: [userAddress],
    address: sellerPenaltyToken.address as Address,
  });
  const { data: sellerExchangeTokenBalance } = useReadErc20BalanceOf({
    args: [userAddress],
    address: sellerExchangeToken.address as Address,
  });
  const { data: sellerFeeTokenBalance } = useReadErc20BalanceOf({
    args: [userAddress],
    address: sellerFeeToken.address as Address,
  });

  const { data: buyerPenaltyTokenBalance } = useReadErc20BalanceOf({
    args: [userAddress],
    address: buyerPenaltyToken.address as Address,
  });
  const { data: buyerExchangeTokenBalance } = useReadErc20BalanceOf({
    args: [userAddress],
    address: buyerExchangeToken.address as Address,
  });
  const { data: buyerFeeTokenBalance } = useReadErc20BalanceOf({
    args: [userAddress],
    address: buyerFeeToken.address as Address,
  });

  return {
    sellerPenaltyTokenApprovedAmount: sellerPenaltyTokenApprovedAmount || 0n,
    sellerExchangeTokenApprovedAmount: sellerExchangeTokenApprovedAmount || 0n,
    sellerFeeTokenApprovedAmount: sellerFeeTokenApprovedAmount || 0n,
    buyerPenaltyTokenApprovedAmount: buyerPenaltyTokenApprovedAmount || 0n,
    buyerExchangeTokenApprovedAmount: buyerExchangeTokenApprovedAmount || 0n,
    buyerFeeTokenApprovedAmount: buyerFeeTokenApprovedAmount || 0n,
    sellerPenaltyTokenBalance: sellerPenaltyTokenBalance || 0n,
    sellerExchangeTokenBalance: sellerExchangeTokenBalance || 0n,
    sellerFeeTokenBalance: sellerFeeTokenBalance || 0n,
    buyerPenaltyTokenBalance: buyerPenaltyTokenBalance || 0n,
    buyerExchangeTokenBalance: buyerExchangeTokenBalance || 0n,
    buyerFeeTokenBalance: buyerFeeTokenBalance || 0n,
  };
};

export default EscrowPage;
